import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { bcIcon } from '../lib/businessCategories';
import { toast } from 'sonner';
import {
  Upload, CheckCircle2, X, ArrowRight, ArrowLeft, Loader2, RefreshCw, Send,
  AlertCircle, Sparkles, MessageSquare, Truck, IndianRupee, Package, Boxes, Receipt,
  Ruler, Grid3x3, MoveHorizontal, Shirt, Palette, Tag, Layers, Info, Trash2,
} from 'lucide-react';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_CONCURRENT = 4;

// Aug 2026 — Thaan (whole-piece) inventory helpers. A thaan row is valid when it
// has a positive length AND a positive whole count. Total meters = Σ(length×count).
const thaanRowValid = (r) => Number(r?.thaan_length) > 0 && Number(r?.number_of_thaans) > 0;
const thaanRowTotal = (r) =>
  thaanRowValid(r) ? Math.round(Number(r.thaan_length) * Number(r.number_of_thaans) * 100) / 100 : 0;
const thaanGrandTotal = (rows) =>
  Math.round((rows || []).reduce((s, r) => s + thaanRowTotal(r), 0) * 100) / 100;
const cleanThaanRows = (rows) =>
  (rows || []).filter(thaanRowValid).map((r) => ({
    thaan_length: Math.round(Number(r.thaan_length) * 100) / 100,
    number_of_thaans: Number(r.number_of_thaans),
    total_meters: thaanRowTotal(r),
  }));

// Jun 2026 — Size-wise stock helpers (apparel categories). A size row is valid
// when it has a non-blank size label AND a positive WHOLE-number quantity.
// Total stock = Σ(quantity).
const APPAREL_CATS = ['Ladies Wear', "Men's Wear", 'Girls Wear', 'Boys Wear'];
const isApparelCat = (c) => APPAREL_CATS.includes((c || '').trim());
const sizeRowValid = (r) => {
  const q = Number(r?.quantity);
  return String(r?.size || '').trim().length > 0 && Number.isInteger(q) && q > 0;
};
const sizeRowQty = (r) => (sizeRowValid(r) ? Number(r.quantity) : 0);
const sizeGrandTotal = (rows) => (rows || []).reduce((s, r) => s + sizeRowQty(r), 0);
const cleanSizeRows = (rows) =>
  (rows || []).filter(sizeRowValid).map((r) => ({
    size: String(r.size).trim(),
    quantity: Number(r.quantity),
  }));

/**
 * FABTEXS AI — Product Creation (Feb 2026 redesign).
 *
 * Philosophy: adapt to the wholesaler's WhatsApp workflow instead of forcing
 * them to fill a long product form. Flow:
 *   1. (optional) Pick category — auto-selected when seller has one business category
 *   2. Upload images (each image = one product, sharing the same AI-parsed details)
 *   3. Paste the same WhatsApp product blurb → Continue
 *   4. AI extracts everything possible; seller only fills MISSING mandatory
 *      fields (price, MOQ, stock, shipping policy) if AI couldn't detect them
 *   5. Ready to Broadcast → creates the products and jumps to Broadcast page
 *      with the new products preselected.
 */
export default function BulkUpload() {
  const { seller } = useAuth();
  const nav = useNavigate();

  // ── Category resolution ────────────────────────────────────
  //   `pickedCatId` now holds the Business Category STRING (e.g. "Ladies
  //   Wear"), NOT a master category ID — see comment on the picker below.
  const [pickedCatId, setPickedCatId] = useState('');
  const [catLoaded, setCatLoaded] = useState(false);
  const [notifyAllBuyers, setNotifyAllBuyers] = useState(true);


  // Feb 2026 refactor: the picker no longer fetches master categories or
  // maps Business Categories to product-type sub-items. Sellers pick one of
  // their onboarded Business Category STRINGS ("Ladies Wear", etc.). The
  // string is stored in `pickedCatId` (kept the state name for minimal
  // churn — the field now holds the Business Category, not a master ID).
  const CANONICAL_BC = new Set(['Fabric', 'Ladies Wear', "Men's Wear", 'Girls Wear', 'Boys Wear', 'Furnishing']);
  const bcList = useMemo(() => {
    // Startup migration guarantees existing sellers have business_categories.
    // We validate against the canonical set here so a stale legacy value
    // (e.g. "Kurti" from a pre-migration cached seller doc) never renders as
    // a picker button and later 400s on the backend.
    const raw = seller?.business_categories?.length
      ? seller.business_categories
      : (seller?.categories || []);
    return Array.from(new Set((raw || []).filter((v) => v && CANONICAL_BC.has(v))));
  }, [seller]);

  useEffect(() => {
    // Auto-apply when the seller has exactly one Business Category.
    if (bcList.length === 1) setPickedCatId(bcList[0]);
    setCatLoaded(true);
  }, [bcList]);

  // ── Step machine ───────────────────────────────────────────
  //  category → images → paste → confirm → creating
  const step = !catLoaded ? 'loading'
    : !pickedCatId ? 'category'
    : null; // dynamic step below (images/paste/confirm/creating)

  const [files, setFiles] = useState([]);
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);       // AI-extracted payload
  const [manual, setManual] = useState({
    // Missing pieces the AI couldn't extract — collected in the "Missing
    // description info" section and appended VERBATIM to the seller's
    // original description before broadcast:
    price: '',              // seller-facing (added to description only if missing)
    gst_status: '',         // 'included'|'extra'|'not_applicable' (backend + optional broadcast suffix)
    shipping_type: '',      // 'free'|'fixed'|'location_based'
    shipping_amount: '',
    // AI backend data — NEVER shown in the customer broadcast:
    moq: '',
    stock: '',
    // ── Thaan (whole-piece) inventory (Aug 2026) ────────────────────
    // stock_type: 'REGULAR' (default) or 'THAAN'. When THAAN, `thaans` holds the
    // seller's real batches and `stock` is auto-calculated from them.
    stock_type: 'REGULAR',
    thaans: [{ thaan_length: '', number_of_thaans: '' }],
    // ── Size-wise stock (Jun 2026) — apparel categories ─────────────
    // Per-size piece counts for Ladies/Men's/Girls/Boys Wear. When non-empty,
    // Available stock is auto-calculated from the sum of quantities.
    size_stock: [{ size: '', quantity: '' }],
    // ── Manual Product Type (Feb 2026, final spec) ──────────────────
    //   Required for every category. Free-text — the seller can type any
    //   value. Placeholder + helper text adapt to the picked category via
    //   PRODUCT_TYPE_HINTS() below. Persists as `product_type` on the
    //   product doc for use in catalogue, search, and buyer-facing views.
    product_type: '',
    // ── Category-specific mandatory fields (Feb 2026, final spec) ──
    // Shown ONLY for the picked Business Category. AI preseeds them if the
    // description mentions the value; seller can override or fill in what's
    // missing. All are mandatory before "Create Products" unlocks — the
    // set of fields required depends on the picked category (see
    // categorySpecificFields() below).
    width: '',              // Fabric only (e.g. "60 inch")
    gsm: '',                // Fabric / Furnishing / Kids Wear
    quantity_unit: '',      // Fabric only (metre | yard | piece)
    dimension: '',          // Furnishing only (e.g. "108 x 108 inches")
    size: '',               // Wear categories (e.g. "M, L, XL" or "1-2 Y, 2-3 Y")
    fabric: '',             // Wear categories (e.g. "Cotton")
    pattern_style: '',      // Wear categories (e.g. "Floral")
    fit: '',                // Wear categories (e.g. "A-line", "Slim Fit")
  });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState([]);      // {product_code, image, id}
  // Completion popup state — { mode: 'partial'|'success', success, failed, total } | null.
  // Set at the end of runUpload(). Never causes a route change; the seller
  // stays on this page until they dismiss / act on the popup.
  const [completion, setCompletion] = useState(null);
  // Collection-creation UI state (Step 3 · Task 2):
  //   'idle' — nothing happening
  //   'creating' | 'generating_cover' | 'saving' — sequential loading labels
  //   'done' — collection created; shows the View Collection CTA + hidden URL
  const [collectionState, setCollectionState] = useState({ phase: 'idle', collection: null });
  // Ticket 275862: AI cover image + AI poster text are opt-in (paid, Universal
  // Key). Off by default; the hero product image and regex text are used.
  const [useAiAssets, setUseAiAssets] = useState(false);
  const [sending, setSending] = useState(false);
  // Web Share API prefetch (Feb 2026): iOS Safari REQUIRES the File to be
  // fully ready in memory when the user's tap fires navigator.share() — any
  // `await` between the click and the share() call throws NotAllowedError.
  // We pre-fetch /send + the poster Blob the moment the success screen
  // renders, then the SEND button just calls navigator.share() synchronously.
  //   { phase: 'idle'|'loading'|'ready'|'error', payload: {file, files, text, url, wa_share_url} | null }
  const [shareArtifacts, setShareArtifacts] = useState({ phase: 'idle', payload: null });
  const cancelRef = useRef(false);
  // ── Async Bulk Upload (Path B) runtime state ────────────────
  const BULK_LS_KEY = 'fabtexs.bulk_batch_id';
  const UPLOAD_CONCURRENCY = 3;      // client→server upload fan-out (ingress-safe)
  const UPLOAD_MAX_ATTEMPTS = 5;     // bounded retries per file before FAILED
  const pollRef = useRef(null);
  const activeBatchRef = useRef(null);
  const settledRef = useRef(false);   // batch reached a definitive terminal state
  const finalizedRef = useRef(false); // batch sealed (no more items expected)
  const filesRef = useRef([]);        // synchronous mirror of `files` for polling
  const [activeBatchId, setActiveBatchId] = useState(null);

  // ── Dropzone ───────────────────────────────────────────────
  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length) toast.warning(`${rejected.length} file(s) rejected (type or size)`);
    setFiles((f) => [
      ...f,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
      })),
    ]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });
  const removeFile = (id) => setFiles((f) => f.filter((x) => x.id !== id));

  // Read a File as a base64-encoded string (no data-URL prefix). Used to
  // give the AI parser a look at the first image so it can infer the specific
  // product type (kurti / saree / bedsheet / etc.) alongside the pasted text.
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = r.result;
      const comma = typeof s === 'string' ? s.indexOf(',') : -1;
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  // Ticket 263610: manual product entry, no AI parsing (owner-approved,
  // preview only). Continue now opens the manual field form directly — the
  // same deterministic checks runAiParse used to gate on (description text,
  // length cap, business category) still apply, but no AI/LLM call is made
  // and no "Auto-fill with AI" option is offered. setParsed({}) is an empty
  // seed object (not AI output) that satisfies showParsed / mandatoryValid's
  // existing !parsed guard so the manual form renders; missing_mandatory
  // stays empty since nothing was AI-parsed, so no manual field is preseeded.
  const continueManualEntry = () => {
    if (!rawText.trim()) { toast.error('Paste your WhatsApp product description'); return; }
    if (rawText.length > 8000) { toast.error('Description too long (max 8000 chars)'); return; }
    if (!pickedCatId) { toast.error('Business category required'); return; }
    setParsed({});
  };

  // ── (Retained, unused) Step 3: Ask AI to parse the pasted blurb ───────────────
  const runAiParse = async () => {
    if (!rawText.trim()) { toast.error('Paste your WhatsApp product description'); return; }
    if (rawText.length > 8000) { toast.error('Description too long (max 8000 chars)'); return; }
    if (!pickedCatId) { toast.error('Business category required'); return; }
    setParsing(true);
    try {
      // Send the first uploaded image (best-effort) so the AI can also infer
      // the specific product type from the visual, not just the text.
      let image_base64_list = [];
      const firstImage = files[0]?.file;
      if (firstImage) {
        try {
          const b64 = await fileToBase64(firstImage);
          if (b64 && typeof b64 === 'string') image_base64_list = [b64];
        } catch { /* non-fatal — AI still works on text alone */ }
      }
      const { data } = await api.post('/catalog/ai-parse-description', {
        text: rawText,
        business_category: pickedCatId,
        image_base64_list,
      });
      setParsed(data);
      // Preseed manual fields from anything AI detected so the seller can just
      // review + tweak. Note: price / gst / shipping are ONLY appended to the
      // broadcast if AI did not detect them in the original text (i.e. the
      // field is in `missing_mandatory`). MOQ + Stock are ALWAYS backend-only.
      setManual((m) => ({
        ...m,
        price: data.price != null ? String(data.price) : '',
        gst_status: data.gst_status || '',
        shipping_type: data.shipping_policy?.type || '',
        shipping_amount: data.shipping_policy?.amount != null ? String(data.shipping_policy.amount) : '',
        moq: data.moq != null ? String(data.moq) : '',
        stock: data.stock != null ? String(data.stock) : '',
        // Manual Product Type — preseed from the AI-inferred type so the
        // seller can accept it with one tap, but stays fully editable
        // (per the frozen "no dropdown, no controlled list" rule).
        product_type: data.inferred_product_type
          ? data.inferred_product_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : (m.product_type || ''),
        // Category-specific — only preseed if AI confidently extracted them.
        // Seller can override; empty stays empty until they fill it in.
        width: data.width || '',
        gsm: data.gsm || '',
        quantity_unit: data.quantity_unit || '',
        dimension: data.dimension || '',
        size: (data.sizes && data.sizes.length) ? data.sizes.join(', ') : '',
        fabric: data.fabric_type || '',
        pattern_style: data.pattern_style || data.printing_technique || '',
        fit: data.fit || '',
      }));
      if (data.missing_mandatory?.length) {
        toast.message(`AI understood most of your message. Please fill ${data.missing_mandatory.length} missing field${data.missing_mandatory.length === 1 ? '' : 's'}.`);
      } else {
        toast.success('AI extracted every detail. Review and click Create Products.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI parsing failed — please retry.');
    } finally {
      setParsing(false);
    }
  };

  // ── Validity check for the "Ready to Broadcast" button ─────
  // Feb 2026 spec (v2): manual entry is ALWAYS the source of truth.
  // AI-extracted values only preseed the form; seller-editable values win.
  // MOQ / Stock / GST are backend-only (never appear in broadcast).
  // Price / Shipping are appended to broadcast ONLY when the seller's raw
  // text didn't already mention them.
  const missing = parsed?.missing_mandatory || [];
  const priceMissing = missing.includes('price');
  const gstMissing = missing.includes('gst_status');
  const shippingMissing = missing.includes('shipping');

  // ── Category-specific mandatory fields (Feb 2026 final spec) ──────────
  // Returns the list of field keys that MUST be filled for the currently
  // picked Business Category. Kids Wear (Girls Wear / Boys Wear) matches
  // the reference image with 5 fields including GSM. Fabric shows Width
  // + GSM + Quantity Unit; Furnishing shows GSM + Dimension.
  const categorySpecificFields = (cat) => {
    switch ((cat || '').trim()) {
      case 'Fabric':      return ['width', 'gsm', 'quantity_unit'];
      case 'Furnishing':  return ['gsm', 'dimension'];
      case 'Ladies Wear':
      case "Men's Wear":  return ['size', 'fabric', 'pattern_style', 'fit'];
      case 'Girls Wear':
      case 'Boys Wear':   return ['size', 'fabric', 'pattern_style', 'fit', 'gsm'];
      default:            return [];
    }
  };

  const mandatoryValid = useMemo(() => {
    if (!parsed) return false;
    if (!(Number(manual.price) > 0)) return false;
    if (!(Number(manual.moq) > 0)) return false;
    // Thaan stock: at least one valid thaan row required; stock is auto-derived.
    if ((manual.stock_type || 'REGULAR') === 'THAAN') {
      if (cleanThaanRows(manual.thaans).length === 0) return false;
    } else if (isApparelCat(pickedCatId) && cleanSizeRows(manual.size_stock).length > 0) {
      // Apparel size-wise stock: aggregate is auto-derived from the rows.
    } else if (!(Number(manual.stock) >= 0)) {
      return false;
    }
    if (!['included', 'extra', 'not_applicable'].includes(manual.gst_status)) return false;
    if (!['free', 'fixed', 'location_based'].includes(manual.shipping_type)) return false;
    if (manual.shipping_type === 'fixed' && !(Number(manual.shipping_amount) >= 0)) return false;
    // Product Type — required for EVERY category (spec §7)
    if (!(manual.product_type || '').trim()) return false;
    // Category-specific — every field returned for the picked category
    // must be non-empty (strings) or > 0 (numeric width/gsm).
    for (const k of categorySpecificFields(pickedCatId)) {
      const v = (manual[k] || '').toString().trim();
      if (!v) return false;
    }
    return true;
  }, [parsed, manual, pickedCatId]);

  // Compose the final broadcast description: seller's original text + ONLY
  // the pieces the seller's raw text did not already mention (price /
  // shipping). GST / MOQ / Stock are NEVER appended — backend-only per spec.
  const buildBroadcastDescription = () => {
    const base = rawText.replace(/\s+$/, '');  // strip trailing blank lines only
    const parts = [base];
    if (priceMissing && Number(manual.price) > 0) {
      parts.push(`\nPrice: ₹${manual.price}/pc`);
    }
    if (shippingMissing) {
      const shipLine = manual.shipping_type === 'free'
  ? 'Free Shipping'
  : manual.shipping_type === 'fixed'
  ? `Fixed Shipping per Product: ₹${manual.shipping_amount || 0} per product`
  : manual.shipping_type === 'location_based'
  ? 'Shipping as per Actuals: The seller will share the actual shipping amount after your order is placed. This amount is payable separately as communicated by the seller.'
  : '';
      if (shipLine) parts.push(`\n${shipLine}`);
    }
    return parts.join('\n');
  };

  // ── Step 5: Create N products (one per image) & jump to Broadcast ──
  //
  // ── Async Bulk Upload (Path B) helpers ──────────────────────
  // Build the shared `meta` (common details) once per batch. The backend
  // worker applies this to every product it creates, plus a lightweight
  // per-image AI product-name pass (ai_name_enabled). Manual values win over
  // AI-parsed seeds, exactly as in the previous synchronous flow.
  const buildMeta = () => {
    const meta = {
      price: Number(manual.price) || 0,
      moq: Number(manual.moq) || 1,
      stock: Number(manual.stock) || 0,
      fabric_type: parsed?.fabric_type || '',
      dispatch_days: 2,
      description: buildBroadcastDescription(),
      colors: (parsed?.colors || []).join(', '),
      ai_name_enabled: false,
    };
    // Aug 2026 — Thaan (whole-piece) inventory. Send the individual batches +
    // auto-derived total stock; the backend re-derives and persists them.
    if ((manual.stock_type || 'REGULAR') === 'THAAN') {
      const rows = cleanThaanRows(manual.thaans);
      if (rows.length) {
        meta.stock_type = 'THAAN';
        meta.thaans = JSON.stringify(rows);
        meta.stock = thaanGrandTotal(manual.thaans);
      }
    }
    // Jun 2026 — Size-wise stock (apparel). Send per-size piece counts + the
    // auto-derived total; the backend re-derives and persists them.
    if (isApparelCat(pickedCatId)) {
      const sizeRows = cleanSizeRows(manual.size_stock);
      if (sizeRows.length) {
        meta.size_stock = JSON.stringify(sizeRows);
        meta.stock = sizeGrandTotal(manual.size_stock);
      }
    }
    if (parsed?.name) meta.name = parsed.name;
    if (parsed?.width) meta.width = parsed.width;
    if (parsed?.gsm) meta.gsm = parsed.gsm;
    if (parsed?.construction) meta.construction = parsed.construction;
    if (parsed?.printing_technique) meta.printing_technique = parsed.printing_technique;
    if (parsed?.wash_care) meta.wash_care = parsed.wash_care;
    if (parsed?.sizes?.length) meta.sizes = parsed.sizes.join(',');
    if (parsed?.saree_length_m != null) meta.saree_length_m = String(parsed.saree_length_m);
    if (parsed?.blouse_piece_included != null) meta.blouse_piece_included = String(parsed.blouse_piece_included);
    if (parsed?.material_details) meta.material_details = parsed.material_details;
    if (parsed?.dupatta_length_m != null) meta.dupatta_length_m = String(parsed.dupatta_length_m);
    if (parsed?.home_furnishing_type) meta.home_furnishing_type = parsed.home_furnishing_type;
    if (parsed?.inferred_product_type) meta.inferred_product_type = parsed.inferred_product_type;
    // Category-specific manual fields (manual overrides AI seeds).
    const catFields = (function () {
      const t = (pickedCatId || '').trim();
      if (t === 'Fabric') return ['width', 'gsm', 'quantity_unit'];
      if (t === 'Furnishing') return ['gsm', 'dimension'];
      if (t === 'Ladies Wear' || t === "Men's Wear") return ['size', 'fabric', 'pattern_style', 'fit'];
      if (t === 'Girls Wear' || t === 'Boys Wear') return ['size', 'fabric', 'pattern_style', 'fit', 'gsm'];
      return [];
    })();
    for (const k of catFields) {
      const v = (manual[k] || '').toString().trim();
      if (!v) continue;
      if (k === 'fabric') meta.fabric_type = v;
      else if (k === 'size') meta.sizes = v;
      else meta[k] = v;
    }
    const shipType = manual.shipping_type || parsed?.shipping_policy?.type || '';
    if (shipType) meta.shipping_policy_type = shipType;
    const shipAmt = manual.shipping_type === 'fixed'
      ? String(manual.shipping_amount || '')
      : (!manual.shipping_type && parsed?.shipping_policy?.amount != null
        ? String(parsed.shipping_policy.amount) : '');
    if (shipAmt) meta.shipping_policy_amount = shipAmt;
    if ((manual.product_type || '').trim()) meta.product_type = manual.product_type.trim();
    const gst = manual.gst_status || parsed?.gst_status || '';
    if (gst) meta.gst_status = gst;
    return meta;
  };

  // Map a single server item state onto a per-file status shape.
  const reconcileFile = (x, byCid) => {
    if (!x.clientItemId) return x;
    const it = byCid[x.clientItemId];
    // No server item for this file → keep local status (true ingest failure).
    if (!it) return x;
    if (it.state === 'DONE') return { ...x, status: 'done', productId: it.product_id, productCode: it.product_code, duplicate: false, error: null };
    if (it.state === 'SKIPPED_DUPLICATE') return { ...x, status: 'done', productId: it.product_id, productCode: it.duplicate_of || 'DUPLICATE', duplicate: true, error: null };
    if (it.state === 'FAILED') return { ...x, status: 'error', error: it.error || 'Processing failed' };
    return { ...x, status: 'uploading' }; // PENDING / CLAIMED / PROCESSING
  };

  // Reconciliation is the SOURCE OF TRUTH: counts are derived from the selected
  // files (each mapped to its authoritative server state), NEVER from raw server
  // totals — so files whose upload never reached the backend are still counted.
  const computeCompletion = (fileList) => {
    const created = fileList.filter((f) => f.status === 'done' && !f.duplicate).length;
    const skipped = fileList.filter((f) => f.status === 'done' && f.duplicate).length;
    const failed = fileList.filter((f) => f.status === 'error').length;
    const selected = fileList.length;
    return { mode: failed > 0 ? 'partial' : 'success', created, skipped, failed, selected };
  };

  // Poll until the batch reaches a DEFINITIVE terminal state: the batch must be
  // sealed (finalized) AND every selected file resolved to done/error (no file
  // left queued/uploading, i.e. no server item still PENDING/PROCESSING). Only
  // then do we surface the completion popup. Invariant enforced by reconciliation:
  // created + skipped + failed === selected before any terminal state.
  const startPolling = (batchId) => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    const tick = async () => {
      let data;
      try {
        ({ data } = await api.get(`/bulk-upload/batches/${batchId}`));
      } catch (e) { return; } // transient network error — keep polling
      const byCid = {};
      (data.items || []).forEach((it) => { byCid[it.client_item_id] = it; });
      const next = filesRef.current.map((x) => reconcileFile(x, byCid));
      filesRef.current = next;
      setFiles(next);
      setCreated((data.items || []).filter((i) => i.product_id).map((i) => ({ id: i.product_id, code: i.product_code || i.duplicate_of })));

      const anyPending = next.some((f) => f.status === 'queued' || f.status === 'uploading');
      if (finalizedRef.current && !anyPending && !settledRef.current) {
        settledRef.current = true;
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setCreating(false);
        const c = computeCompletion(next);
        setCompletion(c);
        // Keep the resume marker while anything failed so retry survives refresh.
        if (c.failed === 0) { try { localStorage.removeItem(BULK_LS_KEY); } catch (e) { /* noop */ } }
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
  };

  const readyToBroadcast = async () => {
    if (!mandatoryValid) { toast.error('Fill all mandatory fields to continue'); return; }
    if (files.length === 0) { toast.error('Add at least one image'); return; }
    await runUpload(files);
  };

  // Shared per-file uploader with bounded exponential-backoff retry. Network
  // errors and 5xx are treated as transient (retried); 4xx is a permanent
  // rejection. Returns true on success.
  const uploadFileToBatch = async (batchId, fe) => {
    setFiles((f) => f.map((x) => (x.id === fe.id ? { ...x, status: 'uploading', error: null } : x)));
    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const fd = new FormData();
        fd.append('file', fe.file);
        fd.append('client_item_id', fe.clientItemId);
        await api.post(`/bulk-upload/batches/${batchId}/items`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        });
        return true;
      } catch (err) {
        const status = err.response?.status;
        if (status && status >= 400 && status < 500) { // permanent rejection
          setFiles((f) => f.map((x) => (x.id === fe.id ? { ...x, status: 'error', error: err.response?.data?.detail || 'Rejected' } : x)));
          return false;
        }
        if (attempt === UPLOAD_MAX_ATTEMPTS) {
          setFiles((f) => f.map((x) => (x.id === fe.id ? { ...x, status: 'error', error: 'Upload failed after retries' } : x)));
          return false;
        }
        await new Promise((r) => setTimeout(r, Math.min(800 * Math.pow(2, attempt - 1), 8000)));
      }
    }
    return false;
  };

  // Runs the async bulk-upload pipeline on a subset of files: create batch →
  // stream each file to object storage (bounded retry) → finalize → poll. The
  // seller stays on this page; the completion popup opens ONLY when the batch
  // reaches a definitive terminal state (see startPolling).
  const runUpload = async (subset) => {
    if (!subset || subset.length === 0) return;
    setCreating(true);
    cancelRef.current = false;
    settledRef.current = false;
    finalizedRef.current = false;
    setCompletion(null);
    // Assign a stable client_item_id per file (idempotency key).
    const genId = () => (window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const withCids = subset.map((s) => ({ ...s, clientItemId: s.clientItemId || genId() }));
    setFiles((f) => {
      const nf = f.map((x) => {
        const s = withCids.find((w) => w.id === x.id);
        return s ? { ...x, status: 'queued', error: null, clientItemId: s.clientItemId } : x;
      });
      filesRef.current = nf;
      return nf;
    });

    // 1. Create the batch (backend validates the category up-front).
    let batchId;
    try {
      const { data } = await api.post('/bulk-upload/batches', {
        business_category: pickedCatId,
        meta: buildMeta(),
      });
      batchId = data.batch_id;
    } catch (err) {
      setCreating(false);
      toast.error(err.response?.data?.detail || 'Could not start the upload. Please try again.');
      return;
    }
    activeBatchRef.current = batchId;
    setActiveBatchId(batchId);
    try { localStorage.setItem(BULK_LS_KEY, batchId); } catch (e) { /* noop */ }

    // 2. Upload each file (bounded fan-out to stay ingress-safe).
    const q = [...withCids];
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, q.length) }, async () => {
      while (q.length && !cancelRef.current) {
        const item = q.shift();
        if (item) await uploadFileToBatch(batchId, item);
      }
    }));

    // 3. Seal the batch, then poll to the definitive terminal state.
    try { await api.post(`/bulk-upload/batches/${batchId}/finalize`); } catch (e) { /* worker still drains */ }
    finalizedRef.current = true;
    startPolling(batchId);
  };

  // ── Retry failed items (Path B) ─────────────────────────────
  // Recovers BOTH failure classes WITHIN the same batch:
  //   1. server-side FAILED items → retry-failed endpoint flips them to PENDING
  //   2. files whose upload never reached the backend → re-upload (idempotent;
  //      requires the original blob, so resumed rows without bytes are covered
  //      by (1) only). All successfully-created products are preserved.
  const retryFailed = async () => {
    const bid = activeBatchRef.current;
    if (!bid) { setCompletion(null); return; }
    setCompletion(null);
    setCreating(true);
    cancelRef.current = false;
    settledRef.current = false;
    try {
      // 1. Reset server-side FAILED rows → PENDING.
      await api.post(`/bulk-upload/batches/${bid}/retry-failed`);
      // 2. Re-upload ingest-failure files that still hold their bytes.
      const toReupload = filesRef.current.filter((f) => f.status === 'error' && f.file);
      setFiles((f) => {
        const nf = f.map((x) => (x.status === 'error' ? { ...x, status: 'queued', error: null } : x));
        filesRef.current = nf;
        return nf;
      });
      if (toReupload.length) {
        const q = [...toReupload];
        await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, q.length) }, async () => {
          while (q.length && !cancelRef.current) { const it = q.shift(); if (it) await uploadFileToBatch(bid, it); }
        }));
      }
      finalizedRef.current = true;
      try { await api.post(`/bulk-upload/batches/${bid}/finalize`); } catch (e) { /* already processing */ }
      startPolling(bid);
    } catch (err) {
      setCreating(false);
      toast.error('Could not retry the failed images. Please try again.');
    }
  };

  // Rebuild per-file rows from server items (used on refresh/resume — the
  // original File blobs are gone, so `file` is null and those rows can only be
  // recovered via the server-side retry path).
  const _synthFilesFromItems = (items) => (items || []).map((it) => ({
    id: it.item_id,
    clientItemId: it.client_item_id,
    name: it.original_filename || it.item_id,
    file: null,
    resumed: true,
    status: (it.state === 'DONE' || it.state === 'SKIPPED_DUPLICATE') ? 'done'
      : it.state === 'FAILED' ? 'error' : 'uploading',
    duplicate: it.state === 'SKIPPED_DUPLICATE',
    productId: it.product_id || null,
    productCode: it.product_code || it.duplicate_of || null,
    error: it.state === 'FAILED' ? (it.error || 'Processing failed') : null,
  }));

  // ── Resume a batch after a browser refresh ──────────────────
  useEffect(() => {
    let bid = null;
    try { bid = localStorage.getItem(BULK_LS_KEY); } catch (e) { /* noop */ }
    if (!bid) return;
    (async () => {
      try {
        const { data } = await api.get(`/bulk-upload/batches/${bid}`);
        const synth = _synthFilesFromItems(data.items);
        if (data.status === 'PROCESSING' || data.status === 'OPEN') {
          activeBatchRef.current = bid;
          setActiveBatchId(bid);
          setPickedCatId(data.business_category || '');
          finalizedRef.current = true;
          settledRef.current = false;
          filesRef.current = synth;
          setFiles(synth);
          setCreating(true);
          startPolling(bid);
          toast.info('Resuming your bulk upload…');
        } else if (data.status === 'COMPLETE_WITH_ERRORS') {
          // Terminal-with-errors: rebuild rows and re-open the partial popup so
          // the seller can still Retry Failed after a refresh.
          activeBatchRef.current = bid;
          setActiveBatchId(bid);
          setPickedCatId(data.business_category || '');
          finalizedRef.current = true;
          settledRef.current = true;
          filesRef.current = synth;
          setFiles(synth);
          setCompletion(computeCompletion(synth));
          toast.info('Some images still need a retry.');
        } else {
          try { localStorage.removeItem(BULK_LS_KEY); } catch (e) { /* noop */ }
        }
      } catch (e) {
        try { localStorage.removeItem(BULK_LS_KEY); } catch (er) { /* noop */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the synchronous mirror in sync with committed files.
  useEffect(() => { filesRef.current = files; }, [files]);

  // Stop polling on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Feb 2026 · Step 3 · Task 2 — Create Collection from the current upload session.
  // Sequences three loading labels for the seller ("Creating…" → "Generating
  // AI cover…" → "Saving…") because the backend does all three in a single
  // synchronous request. The AI cover step is what actually takes time; the
  // "Creating" and "Saving" labels are cosmetic waypoints.
  const createCollection = async () => {
    const productIds = files
      .filter((f) => f.status === 'done' && f.productId)
      .map((f) => f.productId);
    if (productIds.length === 0) {
      toast.error('No products from this session to add to a collection.');
      return;
    }
    setCollectionState({ phase: useAiAssets ? 'generating_cover' : 'saving', collection: null });
    // Bump to "Saving Collection..." after ~4s so the seller sees the sequence
    // land on the saving step even when Gemini returns quickly.
    const bumpToSaving = setTimeout(() => {
      setCollectionState((s) => (s.phase === 'generating_cover' ? { ...s, phase: 'saving' } : s));
    }, 4000);
    try {
      
const { data } = await api.post('/collections', {
    product_ids: productIds,
    description: rawText || '',
    ai_cover: useAiAssets,
    notify_all_buyers: notifyAllBuyers,
});

      clearTimeout(bumpToSaving);
      setCollectionState({ phase: 'done', collection: data });
      toast.success('Collection saved. Send it to your WhatsApp groups.');
    } catch (err) {
      clearTimeout(bumpToSaving);
      setCollectionState({ phase: 'idle', collection: null });
      toast.error(err.response?.data?.detail || 'Could not create collection. Please try again.');
    }
  };

  // SEND — Web Share API compliant prefetch (Feb 2026 iOS Safari bug fix):
  //
  // The iOS Web Share API rejects `navigator.share()` with `NotAllowedError`
  // if there is ANY awaited network call between the user's tap and the
  // share() invocation. Every millisecond spent awaiting `fetch()` or
  // `api.post()` after the click loses the transient-user-activation window.
  //
  // Fix: as soon as the "Successfully Created" screen mounts with a saved
  // collection, we
  //   1) POST /collections/{id}/send  (server compose poster + notify buyers,
  //      idempotent so re-mounts are safe)
  //   2) fetch(catalogue_image)       (retrieve poster as Blob)
  //   3) new File([blob], name)       (build the File object share() wants)
  // and hold it in state. The SEND button's onClick is then completely
  // synchronous — a straight-line `navigator.share({files, text, url})`
  // with a `window.open(wa_share_url)` fallback for browsers without the
  // Web Share API.
  //
  // CRITICAL: no matter what happens in the prefetch, the SEND button must
  // still open WhatsApp. See _buildFallbackWaUrl below — it uses the
  // `share_url` returned inline by POST /collections (available in state
  // the instant the collection is saved, no network required).
  const _buildFallbackWaUrl = (collection) => {
    if (!collection) return '';
    const link = collection.share_url || collection.view_url || '';
    if (!link) return '';
    const text = `🔗 👉 ${link}\n\nTap the above link to explore the complete collection.`;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  };

  useEffect(() => {
    if (collectionState.phase !== 'done' || !collectionState.collection) return;
    if (shareArtifacts.phase !== 'idle') return;
    let cancelled = false;
    setShareArtifacts({ phase: 'loading', payload: null });
    (async () => {
      try {
        console.log('[SEND] prefetch — POST /collections/%s/send', collectionState.collection.id);
        const { data } = await api.post(`/collections/${collectionState.collection.id}/send`, null,
          { params: { ai_extract: useAiAssets } });
        console.log('[SEND] prefetch — /send OK', { hasImage: !!data.catalogue_image, share_url: data.share_url });
        let file = null;
        if (data.catalogue_image) {
          try {
            const resp = await fetch(data.catalogue_image, { credentials: 'omit', mode: 'cors' });
            if (resp.ok) {
              const blob = await resp.blob();
              const fname = `${data.collection_code || 'catalogue'}.jpg`;
              file = new File([blob], fname, { type: blob.type || 'image/jpeg' });
              console.log('[SEND] prefetch — image blob OK (%d bytes)', blob.size);
            } else {
              console.warn('[SEND] prefetch — image fetch not ok', resp.status);
            }
          } catch (imgErr) {
            // Blob prefetch failure is non-fatal. On tap we still open wa.me
            // with the pre-filled caption + link — that's the old behaviour
            // and it works everywhere.
            console.warn('[SEND] prefetch — image fetch failed', imgErr);
          }
        }
        if (cancelled) return;
        setShareArtifacts({
          phase: 'ready',
          payload: {
            file,
            text: data.message_text || '',
            url: data.share_url || data.view_url || '',
            wa_share_url: data.wa_share_url || _buildFallbackWaUrl(collectionState.collection),
            catalogue_image: data.catalogue_image || '',
            collection_code: data.collection_code || '',
          },
        });
      } catch (err) {
        // /send failed entirely — network / auth / 500. Do NOT leave the
        // button broken. Store a synchronous wa.me fallback built from the
        // collection.share_url we already have in state (returned by
        // POST /collections in the previous step).
        console.error('[SEND] prefetch failed — falling back to wa.me text-only', err);
        if (cancelled) return;
        setShareArtifacts({
          phase: 'fallback',
          payload: {
            file: null,
            text: '',
            url: collectionState.collection?.share_url || collectionState.collection?.view_url || '',
            wa_share_url: _buildFallbackWaUrl(collectionState.collection),
            catalogue_image: '',
            collection_code: '',
            error: err.response?.data?.detail || err.message || 'prefetch failed',
          },
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionState.phase, collectionState.collection?.id]);

  // Called synchronously from the SEND button onClick. NO `await` before
  // navigator.share() — that's the whole point of the prefetch above.
  //
  // Golden rule: this button MUST always open WhatsApp. If Web Share is
  // available AND we have a File → rich share sheet with poster attached.
  // Otherwise → fall back to `window.open('https://wa.me/?text=...')` with
  // the caption + link (old, always-worked behaviour).
  // Detect a touch-first device (Android + iOS + touch tablets) at click
  // time. We route touch devices through the Web Share sheet so buyers can
  // pick the recipient from their existing WhatsApp chats/groups; desktop
  // browsers fall straight through to wa.me because there is no share sheet.
  const _isTouchDevice = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  };

  // The SEND button is disabled on touch devices until the poster File has
  // fully finished prefetching — otherwise a mobile seller could tap in the
  // brief window before the Blob arrives and end up on a text-only share.
  const _isSendDisabled = () => {
    if (sending) return true;
    if (!collectionState.collection) return true;
    if (_isTouchDevice()) {
      // Wait for poster File. Never freeze forever — once the prefetch
      // resolves as 'ready' (even without a File, e.g. on Pillow failure) or
      // 'fallback' (whole /send call failed), we unlock and let the wa.me
      // text-only fallback kick in.
      if (shareArtifacts.phase === 'loading' || shareArtifacts.phase === 'idle') return true;
    }
    return false;
  };

  const handleSend = () => {
    if (sending) return;
    const collection = collectionState.collection;
    const collectionLink = collection?.share_url || collection?.view_url || '';
    // Feb 2026 — Well-formed share text. WhatsApp's auto-linkifier is
    // inconsistent on bare URLs pasted into an image caption (Web Share
    // mobile path). Placing the URL on its own line with a leading emoji
    // prompt matches the backend `message_text` template in
    // `send_collection` and forces WhatsApp Android + iOS + desktop to
    // render it as blue-clickable. Same string is used for both the
    // Web-Share (files+text) path and the wa.me (text-only) fallback so
    // buyers see the exact same clickable link across every device.
    const shareText = collectionLink
      ? `🔗 👉 ${collectionLink}\n\nTap the above link to explore the complete collection.`
      : '';
    // Client-side wa.me URL — single-encoded, well-formed, exactly like the
    // old always-worked flow. Used as fallback on desktop + on any share
    // rejection.
    const waFallback = shareText
      ? 'https://wa.me/?text=' + encodeURIComponent(shareText)
      : '';
    const posterFile = shareArtifacts.payload?.file || null;
    const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    const isTouch = _isTouchDevice();

    // Rich mobile share: files + text land in WhatsApp's recipient picker
    // with the poster attached and the collection link in the caption field.
    // Zero network calls between this line and navigator.share() — the File
    // is already in memory from the useEffect prefetch above.
    if (isTouch && canWebShare && posterFile) {
      const shareData = { files: [posterFile], text: shareText };
      let hasFiles = false;
      if (typeof navigator.canShare === 'function') {
        try { hasFiles = navigator.canShare({ files: [posterFile] }); } catch { hasFiles = false; }
      }
      if (hasFiles) {
        setSending(true);
        console.log('[SEND] navigator.share — files+text');
        navigator.share(shareData)
          .catch((err) => {
            if (err && err.name === 'AbortError') return;
            console.warn('[SEND] navigator.share failed — opening wa.me fallback', err);
            if (waFallback) {
              console.log('SEND_DEBUG url:', waFallback);
              window.open(waFallback, '_blank');
            }
          })
          .finally(() => setSending(false));
        return;
      }
      // canShare({files}) returned false — device doesn't accept image share
      // (older Android Web Share Level 1). Fall through to wa.me.
    }

    // Desktop OR mobile without file/canShare support → open wa.me directly.
    // window.open must be the LAST statement (no toast, no state update).
    if (!waFallback) {
      toast.error('Could not build the WhatsApp share link.');
      return;
    }
    console.log('SEND_DEBUG url:', waFallback);
    window.open(waFallback, '_blank');
  };

  // ─── Render ────────────────────────────────────────────────
  if (step === 'loading') {
    return <div className="text-fab-text-muted p-6">Loading categories…</div>;
  }

  if (step === 'category') {
    // Seller has zero Business Categories (edge case — signup enforces ≥1,
    // but legacy accounts may exist). Show a clear onboarding prompt.
    if (bcList.length === 0) {
      return (
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5 fade-up" data-testid="product-create-page">
          <StepChrome active={0} />
          <div className="fab-card p-5 sm:p-7 text-center" data-testid="empty-category-state">
            <div className="text-4xl mb-2">🧭</div>
            <h2 className="font-display font-bold text-lg sm:text-xl text-fab-text">No business categories on file</h2>
            <p className="text-[13px] text-fab-text-secondary mt-2 max-w-md mx-auto">
              Your account doesn&apos;t have any Business Categories selected.
              Please contact support so we can update your account.
            </p>
          </div>
        </div>
      );
    }
    // Multiple Business Categories → pick one. Render the seller's exact
    // onboarded strings as buttons. Never expand into master categories or
    // product-type sub-items.
    return (
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-5 fade-up" data-testid="product-create-page">
        <StepChrome active={0} />
        <div className="fab-card p-4 sm:p-7">
          <h2 className="font-display font-bold text-lg sm:text-xl text-fab-text">Which category are you uploading today?</h2>
          <p className="text-[13px] text-fab-text-secondary mt-1">
            Pick the business category for this batch. FABTEXS AI will figure out the specific product type from your description and images.
          </p>
          <div className="mt-4 sm:mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3" data-testid="business-category-picker">
            {bcList.map((bc) => (
              <button
                key={bc}
                onClick={() => setPickedCatId(bc)}
                data-testid={`bc-pick-${bc.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="p-3 sm:p-4 rounded-xl border-2 border-fab-primary-lighter bg-white hover:border-fab-primary transition-all text-left"
              >
                <div className="text-2xl sm:text-3xl mb-1">{bcIcon(bc)}</div>
                <div className="font-display font-bold text-fab-text text-[13px] sm:text-[15px]">{bc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Main flow: images → paste → (parsed summary + missing) → creating ──
  const showParsed = Boolean(parsed);
  const showCreating = creating || files.some((f) => f.status === 'uploading' || f.status === 'done' || f.status === 'error');

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-5 fade-up" data-testid="product-create-page">
      <StepChrome active={showCreating ? 3 : showParsed ? 2 : 1} />

      {/* ── Category header + change link ───────────────────── */}
      <div className="fab-card p-3 flex items-center justify-between gap-2" data-testid="picked-cat-strip">
        <div className="flex items-center gap-2 text-[13px] text-fab-text min-w-0">
          <span className="text-xl shrink-0">{bcIcon(pickedCatId)}</span>
          <div className="min-w-0">
            <div className="font-semibold truncate">Business Category: {pickedCatId}</div>
            <div className="text-[11px] text-fab-text-muted hidden sm:block">FABTEXS AI will detect the specific product type automatically.</div>
          </div>
        </div>
        {bcList.length > 1 && !showCreating && (
          <button
            onClick={() => { setPickedCatId(''); setParsed(null); setFiles([]); setRawText(''); }}
            className="text-[12px] text-fab-primary font-semibold flex items-center gap-1 shrink-0 whitespace-nowrap px-2 py-2 rounded-lg hover:bg-fab-elevated"
            data-testid="change-category-btn"
          ><ArrowLeft className="w-3 h-3" /> <span className="hidden sm:inline">Change category</span><span className="sm:hidden">Change</span></button>
        )}
      </div>

      {/* ── Section 1: Images ───────────────────────────────── */}
      {!showCreating && (
        <div className="fab-card p-4 sm:p-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-fab-primary text-white flex items-center justify-center text-[12px] font-bold shrink-0">1</div>
            <h2 className="font-display font-bold text-base sm:text-lg text-fab-text">Upload product images</h2>
          </div>
          <p className="text-[12px] sm:text-[13px] text-fab-text-secondary mt-1 sm:ml-9">
            Each image becomes one product. All products share the details you paste below.
            JPG / PNG / WEBP up to 10 MB each.
          </p>
          <div
            {...getRootProps()}
            data-testid="dropzone"
            className={`mt-4 border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center cursor-pointer transition-colors ${isDragActive ? 'border-fab-primary bg-fab-primary-lighter' : 'border-fab-primary-lighter hover:bg-fab-elevated'}`}
          >
            <input {...getInputProps()} data-testid="file-input" />
            <Upload className="w-8 h-8 text-fab-primary mx-auto mb-2" />
            <div className="font-semibold text-fab-text text-[13px] sm:text-[14px]">
              {isDragActive ? 'Drop images here' : 'Tap to browse or drag & drop images'}
            </div>
          </div>
          {files.length > 0 && (
            <div className="mt-4">
              <div className="text-[12px] font-semibold text-fab-text-secondary mb-2">
                {files.length} image{files.length === 1 ? '' : 's'} selected · will create {files.length} product{files.length === 1 ? '' : 's'}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-[200px] overflow-y-auto">
                {files.map((f) => (
                  <div key={f.id} className="relative aspect-square rounded-lg overflow-hidden group">
                    <img src={f.preview} alt={f.file.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFile(f.id)}
                      className="absolute top-1 right-1 w-6 h-6 sm:w-5 sm:h-5 rounded-full bg-black/60 text-white flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100"
                      data-testid={`remove-${f.id}`}
                    ><X className="w-3.5 h-3.5 sm:w-3 sm:h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Section 2: Paste description ─────────────────────── */}
      {!showCreating && !showParsed && (
        <div className="fab-card p-4 sm:p-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-fab-primary text-white flex items-center justify-center text-[12px] font-bold shrink-0">2</div>
            <h2 className="font-display font-bold text-base sm:text-lg text-fab-text">Product description</h2>
          </div>
          <label className="text-[12px] font-semibold text-fab-text-secondary sm:ml-9 mt-2 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 shrink-0" /> <span>Paste the WhatsApp message you send to buyers</span>
          </label>
          <textarea
            data-testid="ai-paste-textarea"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste the same WhatsApp product message that you normally send to your buyers. FABTEXS AI will automatically understand the details."
            rows={8}
            className="fab-input mt-2 w-full px-3 sm:px-4 py-3 rounded-xl border border-fab-primary-lighter text-[14px] leading-relaxed"
          />
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-[11px] text-fab-text-muted">
              {rawText.length}/8000 · You'll fill in fabric, price, MOQ, stock, colors and sizes manually next.
            </div>
            <button
              data-testid="ai-continue-btn"
              onClick={continueManualEntry}
              disabled={!rawText.trim() || files.length === 0}
              className="btn-primary text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-50 w-full sm:w-auto"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {files.length === 0 && (
            <div className="mt-2 text-[11px] text-fab-warning">Please upload at least one image first.</div>
          )}
        </div>
      )}

      {/* ── Section 3: Parsed summary + missing mandatory fields ─── */}
      {!showCreating && showParsed && (
        <ParsedSummary
          parsed={parsed}
          manual={manual}
          setManual={setManual}
          rawText={rawText}
          reset={() => { setParsed(null); }}
          onReady={readyToBroadcast}
          canReady={mandatoryValid && files.length > 0}
          fileCount={files.length}
          pickedCatId={pickedCatId}
        />
      )}

      {/* ── Section 4: Creating ─────────────────────────────── */}
      {showCreating && (
        <div className="fab-card p-4 sm:p-6" data-testid="creating-state">
          <h2 className="font-display font-bold text-base sm:text-lg text-fab-text flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-fab-primary shrink-0" />
            <span>Creating {files.length} product{files.length === 1 ? '' : 's'}…</span>
          </h2>
          <p className="text-[12px] sm:text-[13px] text-fab-text-secondary mt-1">Uploading images, applying AI-extracted details, generating codes.</p>
          <div className="mt-4 sm:mt-5 grid grid-cols-3 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {files.map((f) => (
              <div key={f.id} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${f.status === 'done' ? 'border-fab-wa' : f.status === 'error' ? 'border-fab-error' : 'border-fab-primary-lighter'}`}>
                <img src={f.preview} alt="" className="w-full h-full object-cover" />
                {f.status === 'uploading' && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                  </div>
                )}
                {f.status === 'done' && (
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-fab-wa text-white text-[9px] font-mono text-center">
                    {f.productCode}
                  </div>
                )}
                {f.status === 'error' && (
                  <div className="absolute inset-0 bg-fab-error/80 flex items-center justify-center p-1">
                    <div className="text-[9px] text-white text-center">{(f.error || 'failed').slice(0, 40)}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 text-[12px] text-fab-text-muted">
            Created {created.length} / {files.length}.
          </div>
        </div>
      )}

      {/* ── Completion popup (Feb 2026 spec — replaces auto-redirect) ── */}
      {completion && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4"
          data-testid="upload-completion-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-completion-title-el"
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {completion.mode === 'partial' ? (
              <>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <div id="upload-completion-title-el" className="font-display font-bold text-fab-text text-[16px]" data-testid="upload-completion-title">
                      {completion.created} of {completion.selected} product{completion.selected === 1 ? '' : 's'} created
                    </div>
                    <div className="text-[13px] text-fab-text-secondary mt-2" data-testid="upload-completion-summary">
                      {completion.failed} failed{completion.skipped > 0 ? ` · ${completion.skipped} skipped as duplicate${completion.skipped === 1 ? '' : 's'}` : ''}.
                      Retry the failed image{completion.failed === 1 ? '' : 's'} — already-created products are preserved.
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                  <button
                    onClick={retryFailed}
                    data-testid="upload-completion-retry"
                    className="btn-primary w-full py-2.5 text-[14px] inline-flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Retry Failed ({completion.failed})
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-fab-text text-[16px]" data-testid="upload-completion-title">
                      Successfully Created
                    </div>
                    <div className="text-[13px] text-fab-text-secondary mt-2" data-testid="upload-completion-summary">
                      All {completion.selected} image{completion.selected === 1 ? '' : 's'} processed — {completion.created} product{completion.created === 1 ? '' : 's'} created{completion.skipped > 0 ? `, ${completion.skipped} skipped as duplicate${completion.skipped === 1 ? '' : 's'}` : ''}. Your products are ready.
                    </div>
                  </div>
                </div>
                <div className="mt-5">
                
{collectionState.phase !== 'done' && (
  <label className="flex items-center gap-2 mb-3 text-[13px] text-fab-text-secondary">
    <input
      type="checkbox"
      checked={notifyAllBuyers}
      onChange={(e) => setNotifyAllBuyers(e.target.checked)}
      className="h-4 w-4 accent-current"
    />
    <span>Send notification to all buyers</span>
  </label>
)}
  
                  
                  {collectionState.phase !== 'done' && (
                    <button
                      onClick={createCollection}
                      disabled={collectionState.phase !== 'idle'}
                      data-testid="upload-completion-create-collection"
                      className="btn-primary w-full py-2.5 text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {collectionState.phase === 'idle' && 'Create Collection'}
                      {collectionState.phase === 'generating_cover' && (<><Loader2 className="w-4 h-4 animate-spin" /> Generating AI Catalogue Image...</>)}
                      {collectionState.phase === 'saving' && (<><Loader2 className="w-4 h-4 animate-spin" /> Saving Collection...</>)}
                    </button>
                  )}
                  {collectionState.phase === 'done' && (
                    <div className="text-center text-[13px] font-semibold text-emerald-700 flex items-center justify-center gap-2 py-1" data-testid="collection-saved-status">
                      <CheckCircle2 className="w-4 h-4" /> Collection Saved Successfully
                    </div>
                  )}
                  {collectionState.phase === 'done' && collectionState.collection && (
                    <button
                      onClick={handleSend}
                      disabled={_isSendDisabled()}
                      data-testid="upload-completion-send"
                      className="mt-3 w-full py-2.5 text-[14px] font-semibold rounded-xl bg-fab-wa hover:bg-fab-wa-dark text-white flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {sending ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</>
                      ) : (_isTouchDevice() && !shareArtifacts.payload?.file && (shareArtifacts.phase === 'loading' || shareArtifacts.phase === 'idle')) ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Preparing poster…</>
                      ) : (
                        <><Send className="w-4 h-4" /> SEND</>
                      )}
                    </button>
                  )}
                
                

{collectionState.phase === 'done' && collectionState.collection && (
  <button
    type="button"
    onClick={() => { window.location.href = '/dashboard'; }}
    className="mt-3 w-full py-2.5 text-[14px] font-semibold rounded-xl border border-fab-border text-fab-text hover:bg-fab-surface"
  >
    Done
  </button>
)}
</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ─────────────────────────────────────── */

function StepChrome({ active }) {
  const steps = [
    { full: 'Category', short: 'Category' },
    { full: 'Images & Description', short: 'Images' },
    { full: 'Review AI Details', short: 'Review' },
    { full: 'Create & Broadcast', short: 'Create' },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid="step-chrome">
      {steps.map((s, i) => (
        <div key={s.full} className="flex-1 min-w-0">
          <div
            className={`h-1.5 rounded-full ${i <= active ? 'bg-fab-primary' : 'bg-fab-primary-lighter'}`}
          />
          <div className={`text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold mt-1 truncate ${i === active ? 'text-fab-primary' : 'text-fab-text-muted'}`}>
            <span className="sm:hidden">{i + 1}. {s.short}</span>
            <span className="hidden sm:inline">{i + 1}. {s.full}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ParsedSummary({ parsed, manual, setManual, rawText, reset, onReady, canReady, fileCount, pickedCatId }) {
  const missing = parsed.missing_mandatory || [];
  const isMissing = (k) => missing.includes(k);
  // Feb 2026 refactor: category-specific chips are driven by the AI's
  // inferred_product_type (kurti/saree/dupatta/etc.) rather than a master
  // category slug — the seller only picked a coarse Business Category.
  const type = (parsed.inferred_product_type || '').toLowerCase();
  const isSaree = type === 'saree';
  const isDupatta = type === 'dupatta';
  const isDressMaterial = type === 'dress_material';
  const isHomeFurnishing = type.includes('cushion') || type.includes('curtain') || type.includes('bedsheet') || type === 'home_furnishing';
  // Jun 2026 — stock auto-derivation: Fabric uses Thaan, apparel uses Size Stock.
  const sizeStockActive = isApparelCat(pickedCatId) && cleanSizeRows(manual.size_stock).length > 0;
  const stockAuto = manual.stock_type === 'THAAN' || sizeStockActive;
  const autoStockValue = manual.stock_type === 'THAAN'
    ? thaanGrandTotal(manual.thaans)
    : sizeGrandTotal(manual.size_stock);
  return (
    <>
      <div className="fab-card p-4 sm:p-6" data-testid="parsed-summary">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-fab-primary text-white flex items-center justify-center text-[12px] font-bold shrink-0">3</div>
          <h2 className="font-display font-bold text-base sm:text-lg text-fab-text">FABTEXS AI understood this</h2>
          <button onClick={reset} className="ml-auto text-[12px] text-fab-primary font-semibold whitespace-nowrap" data-testid="edit-description-btn">
            Edit<span className="hidden sm:inline"> description</span>
          </button>
        </div>

        <div className="sm:ml-9 space-y-3">
          {parsed.name && <Chip label="Name" value={parsed.name} />}
          <Chip label="AI description" value={parsed.description || rawText} multiline />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {parsed.fabric_type && <Chip label="Fabric" value={parsed.fabric_type} />}
            {parsed.width && <Chip label="Width" value={parsed.width} />}
            {parsed.gsm && <Chip label="GSM" value={parsed.gsm} />}
            {parsed.construction && <Chip label="Construction" value={parsed.construction} />}
            {parsed.printing_technique && <Chip label="Printing" value={parsed.printing_technique} />}
            {parsed.wash_care && <Chip label="Wash care" value={parsed.wash_care} />}
            {isSaree && parsed.saree_length_m != null && <Chip label="Saree length" value={`${parsed.saree_length_m} m`} />}
            {isSaree && parsed.blouse_piece_included != null && <Chip label="Blouse piece" value={parsed.blouse_piece_included ? 'Included' : 'Not included'} />}
            {isDupatta && parsed.dupatta_length_m != null && <Chip label="Length" value={`${parsed.dupatta_length_m} m`} />}
            {isDressMaterial && parsed.material_details && <Chip label="Material details" value={parsed.material_details} multiline />}
            {isHomeFurnishing && parsed.home_furnishing_type && <Chip label="Type" value={parsed.home_furnishing_type} />}
            {parsed.inferred_product_type && <Chip label="AI product type" value={parsed.inferred_product_type.replace(/_/g, ' ')} />}
          </div>

          {parsed.colors?.length > 0 && (
            <div>
              <div className="text-[10px] text-fab-text-muted uppercase tracking-wider font-semibold mb-1">Colors detected</div>
              <div className="flex flex-wrap gap-1.5">
                {parsed.colors.map((c) => (
                  <span key={c} className="px-2 py-0.5 rounded-md bg-fab-primary-lighter text-fab-primary text-[11px] font-semibold">{c}</span>
                ))}
              </div>
            </div>
          )}
          {parsed.sizes?.length > 0 && (
            <div>
              <div className="text-[10px] text-fab-text-muted uppercase tracking-wider font-semibold mb-1">Sizes detected</div>
              <div className="flex flex-wrap gap-1.5">
                {parsed.sizes.map((s) => (
                  <span key={s} className="px-2 py-0.5 rounded-md bg-fab-elevated text-fab-text-secondary text-[11px] font-semibold">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mandatory fields — always visible so the seller can also correct AI. */}
      <div className="fab-card p-4 sm:p-6" data-testid="mandatory-fields">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-fab-primary text-white flex items-center justify-center text-[12px] font-bold shrink-0">4</div>
          <h2 className="font-display font-bold text-base sm:text-lg text-fab-text">
            {missing.length ? `Fill ${missing.length} missing detail${missing.length === 1 ? '' : 's'}` : 'Confirm the mandatory details'}
          </h2>
        </div>
        <p className="text-[12px] sm:text-[13px] text-fab-text-secondary mt-1 sm:ml-9">
          Everything else was captured by AI. You can also correct anything AI got wrong.
        </p>

        <div className="sm:ml-9 mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <MandatoryField
            icon={<IndianRupee className="w-3.5 h-3.5" />}
            label="Price (₹)"
            highlight={isMissing('price')}
            type="number"
            step="0.01"
            value={manual.price}
            onChange={(v) => setManual({ ...manual, price: v })}
            testid="mandatory-price"
          />
          <MandatoryField
            icon={<Package className="w-3.5 h-3.5" />}
            label="MOQ"
            highlight={isMissing('moq')}
            type="number"
            value={manual.moq}
            onChange={(v) => setManual({ ...manual, moq: v })}
            testid="mandatory-moq"
          />
          <MandatoryField
            icon={<Boxes className="w-3.5 h-3.5" />}
            label={stockAuto ? 'Available stock (auto)' : 'Available stock'}
            highlight={!stockAuto && isMissing('stock')}
            type="number"
            value={stockAuto ? autoStockValue : manual.stock}
            onChange={(v) => setManual({ ...manual, stock: v })}
            testid="mandatory-stock"
            disabled={stockAuto}
          />
        </div>

        <div className="sm:ml-9 mt-4">
          <div className="text-[12px] font-semibold text-fab-text-secondary flex items-center gap-1.5 mb-2 flex-wrap">
            <Receipt className="w-3.5 h-3.5" />
            GST Status
            <span className="px-1.5 py-0.5 rounded bg-fab-elevated text-fab-text-muted text-[10px] font-bold uppercase">Backend only</span>
            {isMissing('gst_status') && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">AI missed</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              ['included', 'GST Included'],
              ['extra', 'GST Extra'],
              ['not_applicable', 'GST Not Applicable'],
            ].map(([val, label]) => (
              <button
                key={val}
                data-testid={`gst-${val}`}
                onClick={() => setManual({ ...manual, gst_status: val })}
                className={`p-2.5 sm:p-3 rounded-xl border-2 text-left text-[13px] font-semibold transition-colors ${
                  manual.gst_status === val ? 'border-fab-primary bg-fab-primary-lighter text-fab-primary' : 'border-fab-primary-lighter bg-white text-fab-text-secondary hover:bg-fab-elevated'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-fab-text-muted mt-1.5">
            Used internally for order and payment calculation. Never shown in the customer broadcast.
          </p>
        </div>

        <div className="sm:ml-9 mt-4">
          <div className="text-[12px] font-semibold text-fab-text-secondary flex items-center gap-1.5 mb-2 flex-wrap">
            <Truck className="w-3.5 h-3.5" />
            Shipping policy
            {isMissing('shipping') && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">AI missed</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              ['free', 'Free Shipping'],
              ['fixed', 'Fixed Shipping per Product'],
              ['location_based', 'Shipping as per Actuals'],
            ].map(([val, label]) => (
              <button
                key={val}
                data-testid={`shipping-${val}`}
                onClick={() => setManual({ ...manual, shipping_type: val })}
                className={`p-2.5 sm:p-3 rounded-xl border-2 text-left text-[13px] font-semibold transition-colors ${
                  manual.shipping_type === val ? 'border-fab-primary bg-fab-primary-lighter text-fab-primary' : 'border-fab-primary-lighter bg-white text-fab-text-secondary hover:bg-fab-elevated'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {manual.shipping_type === 'fixed' && (
            <div className="mt-2">
              <label className="text-[12px] font-semibold text-fab-text-secondary">Fixed charge amount (₹)</label>
              <input
                data-testid="shipping-fixed-amount"
                type="number"
                min="0"
                step="1"
                value={manual.shipping_amount}
                onChange={(e) => setManual({ ...manual, shipping_amount: e.target.value })}
                className="fab-input mt-1 w-full sm:w-40 px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
              />
            </div>
          )}
        </div>

        {/* ── Product details (left) + Thaan Stock Details (right) ──────
            Two-column layout per the approved reference: left holds Product
            Type / Width / GSM / Quantity Unit; right holds the Thaan card. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          <div className="space-y-5">
            <ProductTypeField
              pickedCatId={pickedCatId}
              value={manual.product_type}
              onChange={(v) => setManual({ ...manual, product_type: v })}
            />
            <CategorySpecificFields
              pickedCatId={pickedCatId}
              manual={manual}
              setManual={setManual}
            />
          </div>
          <ThaanStockSection manual={manual} setManual={setManual} pickedCatId={pickedCatId} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
        <div className="text-[12px] text-fab-text-muted sm:mr-auto">
          Will create <strong className="text-fab-text">{fileCount}</strong> product{fileCount === 1 ? '' : 's'}. Products can be edited any time.
        </div>
        <button
          data-testid="ready-to-broadcast-btn"
          onClick={onReady}
          disabled={!canReady}
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-lg bg-fab-wa hover:bg-fab-wa-dark text-white text-[14px] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <CheckCircle2 className="w-4 h-4" /> Create Products
        </button>
      </div>
    </>
  );
}

function Chip({ label, value, multiline = false }) {
  return (
    <div className={multiline ? 'col-span-full' : ''}>
      <div className="text-[10px] text-fab-text-muted uppercase tracking-wider font-semibold">{label}</div>
      <div className={`text-[13px] text-fab-text ${multiline ? 'whitespace-pre-wrap bg-fab-elevated rounded-lg p-2 mt-1' : 'mt-0.5'}`}>{value}</div>
    </div>
  );
}

function MandatoryField({ label, value, onChange, type = 'text', step, icon, highlight, testid, disabled = false }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-fab-text-secondary flex items-center gap-1.5">
        {icon}{label}
        {highlight && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">AI missed</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`fab-input mt-1 w-full px-3 py-2 rounded-lg border text-[13px] ${disabled ? 'bg-fab-elevated text-fab-text-muted cursor-not-allowed border-fab-primary-lighter' : highlight ? 'border-amber-300 focus:border-amber-500' : 'border-fab-primary-lighter'}`}
      />
    </div>
  );
}


// ── Thaan (whole-piece) stock details (Aug 2026) ─────────────────────────────
// Sellers enter their real thaan inventory: multiple batches of
// (length × count). Fabric is sold in complete thaans only — no cutting. Total
// stock is auto-calculated from the batches and drives the product's Available
// stock. Bordered purple card per the approved reference; FABTEXS purple accents.
function ThaanStockSection({ manual, setManual, pickedCatId }) {
  const cat = (pickedCatId || '').trim();
  // Category routing: Fabric → Thaan; Ladies/Men's/Girls/Boys Wear → Size Stock;
  // Furnishing (and anything else) → no inventory sub-section.
  if (isApparelCat(cat)) return <SizeStockSection manual={manual} setManual={setManual} />;
  if (cat !== 'Fabric') return null;
  const isThaan = (manual.stock_type || 'REGULAR') === 'THAAN';
  const rows = manual.thaans && manual.thaans.length ? manual.thaans : [{ thaan_length: '', number_of_thaans: '' }];
  const total = thaanGrandTotal(rows);

  const setRows = (next) => setManual({ ...manual, thaans: next });
  const setType = (t) => setManual({
    ...manual,
    stock_type: t,
    thaans: t === 'THAAN' && rows.length === 0 ? [{ thaan_length: '', number_of_thaans: '' }] : rows,
  });
  const updateRow = (i, key, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setRows([...rows, { thaan_length: '', number_of_thaans: '' }]);
  const deleteRow = (i) => setRows(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);

  return (
    <div className="rounded-2xl border-2 border-fab-primary/50 p-5" data-testid="thaan-section">
      {/* Heading + info + cutting badge */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-bold text-fab-primary uppercase tracking-wide">Thaan Stock Details</span>
          <Info className="w-3.5 h-3.5 text-fab-text-muted" />
        </div>
        {isThaan && (
          <span data-testid="thaan-cutting-indicator" className="inline-flex items-center px-2.5 py-1 rounded-full bg-fab-primary-lighter text-fab-primary text-[11px] font-semibold">
            Cutting Not Allowed
          </span>
        )}
      </div>
      <p className="text-[12px] text-fab-text-secondary mt-1.5 leading-relaxed">
        Add your thaans here. AI will calculate orders using actual thaan quantities.
        Fabric will be sold in complete thaans only (no cutting).
      </p>

      {/* Stock Type dropdown */}
      <div className="mt-4">
        <label className="text-[12px] font-semibold text-fab-text-secondary">Stock Type</label>
        <select
          data-testid="thaan-stock-type"
          value={manual.stock_type || 'REGULAR'}
          onChange={(e) => setType(e.target.value)}
          className="fab-input mt-1 w-full px-3 py-2.5 rounded-lg border border-fab-primary-lighter text-[13px] bg-white"
        >
          <option value="REGULAR">Regular Stock</option>
          <option value="THAAN">Thaan (Whole Piece)</option>
        </select>
      </div>

      {isThaan && (
        <div className="mt-4" data-testid="thaan-table">
          {/* Header row */}
          <div className="hidden sm:grid grid-cols-[28px_1fr_20px_1fr_20px_1fr_28px] gap-2 items-center px-1 pb-1.5 text-[11px] font-semibold text-fab-text-muted">
            <div>#</div>
            <div className="flex items-center gap-1">Thaan Length (meters) <Info className="w-3 h-3" /></div>
            <div></div>
            <div className="flex items-center gap-1">Number of Thaans <Info className="w-3 h-3" /></div>
            <div></div>
            <div>Total (meters)</div>
            <div></div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[20px_1fr_16px_1fr_16px_1fr_28px] sm:grid-cols-[28px_1fr_20px_1fr_20px_1fr_28px] gap-2 items-center py-1.5" data-testid={`thaan-row-${i}`}>
              <div className="text-[13px] text-fab-text-muted">{i + 1}</div>
              <input
                data-testid={`thaan-length-${i}`}
                type="number" min="0" step="0.01" placeholder="10.10"
                value={r.thaan_length}
                onChange={(e) => updateRow(i, 'thaan_length', e.target.value)}
                className="fab-input w-full px-2.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
              />
              <div className="text-center text-[13px] text-fab-text-muted">×</div>
              <input
                data-testid={`thaan-count-${i}`}
                type="number" min="0" step="1" placeholder="10"
                value={r.number_of_thaans}
                onChange={(e) => updateRow(i, 'number_of_thaans', e.target.value)}
                className="fab-input w-full px-2.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
              />
              <div className="text-center text-[13px] text-fab-text-muted">=</div>
              <div data-testid={`thaan-row-total-${i}`} className="text-[13px] font-semibold text-fab-text px-1">
                {thaanRowTotal(r).toFixed(2)}
              </div>
              <button
                type="button"
                data-testid={`thaan-delete-${i}`}
                onClick={() => deleteRow(i)}
                aria-label="Delete thaan row"
                className="inline-flex items-center justify-center text-red-500 hover:text-red-600 disabled:opacity-40"
                disabled={rows.length <= 1}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            data-testid="thaan-add-row"
            onClick={addRow}
            className="mt-2 w-full py-2.5 rounded-lg border border-dashed border-fab-primary/40 text-[13px] font-semibold text-fab-primary hover:bg-fab-primary-lighter/40 transition-colors"
          >
            + Add Thaan
          </button>

          <div className="mt-4 flex items-center justify-between pt-1">
            <span className="text-[12px] font-semibold text-fab-text-secondary">Total Stock (Auto calculated)</span>
            <span data-testid="thaan-total-stock" className="text-[15px] font-bold text-fab-primary">
              {total.toFixed(2)} meters
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Size-wise stock details (Jun 2026) — apparel categories ─────────────────
// Ladies / Men's / Girls / Boys Wear sell per size. Sellers add rows of
// (size × quantity); total pieces auto-calculate and drive Available stock.
function SizeStockSection({ manual, setManual }) {
  const rows = manual.size_stock && manual.size_stock.length ? manual.size_stock : [{ size: '', quantity: '' }];
  const total = sizeGrandTotal(rows);

  const setRows = (next) => setManual({ ...manual, size_stock: next });
  const updateRow = (i, key, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setRows([...rows, { size: '', quantity: '' }]);
  const deleteRow = (i) => setRows(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);

  return (
    <div className="rounded-2xl border-2 border-fab-primary/50 p-5" data-testid="size-stock-section">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-bold text-fab-primary uppercase tracking-wide">Size Stock Details</span>
          <Info className="w-3.5 h-3.5 text-fab-text-muted" />
        </div>
      </div>
      <p className="text-[12px] text-fab-text-secondary mt-1.5 leading-relaxed">
        Add stock for each size. Total available stock is auto-calculated from the
        sum of all sizes.
      </p>

      <div className="mt-4" data-testid="size-stock-table">
        {/* Header row */}
        <div className="hidden sm:grid grid-cols-[28px_1fr_20px_1fr_28px] gap-2 items-center px-1 pb-1.5 text-[11px] font-semibold text-fab-text-muted">
          <div>#</div>
          <div className="flex items-center gap-1">Size <Info className="w-3 h-3" /></div>
          <div></div>
          <div className="flex items-center gap-1">Quantity (pieces) <Info className="w-3 h-3" /></div>
          <div></div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[20px_1fr_16px_1fr_28px] sm:grid-cols-[28px_1fr_20px_1fr_28px] gap-2 items-center py-1.5" data-testid={`size-row-${i}`}>
            <div className="text-[13px] text-fab-text-muted">{i + 1}</div>
            <input
              data-testid={`size-label-${i}`}
              type="text" placeholder="S, M, L, XL…"
              value={r.size}
              onChange={(e) => updateRow(i, 'size', e.target.value)}
              className="fab-input w-full px-2.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px] uppercase"
            />
            <div className="text-center text-[13px] text-fab-text-muted">×</div>
            <input
              data-testid={`size-qty-${i}`}
              type="number" min="0" step="1" placeholder="25"
              value={r.quantity}
              onChange={(e) => updateRow(i, 'quantity', e.target.value)}
              className="fab-input w-full px-2.5 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
            />
            <button
              type="button"
              data-testid={`size-delete-${i}`}
              onClick={() => deleteRow(i)}
              aria-label="Delete size row"
              className="inline-flex items-center justify-center text-red-500 hover:text-red-600 disabled:opacity-40"
              disabled={rows.length <= 1}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        <button
          type="button"
          data-testid="size-add-row"
          onClick={addRow}
          className="mt-2 w-full py-2.5 rounded-lg border border-dashed border-fab-primary/40 text-[13px] font-semibold text-fab-primary hover:bg-fab-primary-lighter/40 transition-colors"
        >
          + Add Size
        </button>

        <div className="mt-4 flex items-center justify-between pt-1">
          <span className="text-[12px] font-semibold text-fab-text-secondary">Total Stock (Auto calculated)</span>
          <span data-testid="size-total-stock" className="text-[15px] font-bold text-fab-primary">
            {total} pieces
          </span>
        </div>
      </div>
    </div>
  );
}


// ── Category-specific mandatory fields (Feb 2026 final reference) ────────
// Renders ONLY the fields belonging to the currently picked Business
// Category — the visual treatment mirrors the approved reference images:
// section separated from the common fields by a top border, purple icons,
// existing FABTEXS input styling, and the same 3-column grid used by the
// common fields (collapses to a single column on mobile so nothing
// overflows). Each field has a placeholder taken from the reference image.
function CategorySpecificFields({ pickedCatId, manual, setManual }) {
  const cat = (pickedCatId || '').trim();
  const fieldSet = (() => {
    if (cat === 'Fabric')                                return ['width', 'gsm', 'quantity_unit'];
    if (cat === 'Furnishing')                            return ['gsm', 'dimension'];
    if (cat === 'Ladies Wear' || cat === "Men's Wear")   return ['size', 'fabric', 'pattern_style', 'fit'];
    if (cat === 'Girls Wear' || cat === 'Boys Wear')     return ['size', 'fabric', 'pattern_style', 'fit', 'gsm'];
    return [];
  })();
  if (!fieldSet.length) return null;

  // Kids Wear (Girls/Boys) uses the "1-2 Y" size placeholder from the reference.
  const isKidsWear = cat === 'Girls Wear' || cat === 'Boys Wear';

  const FIELD_MAP = {
    width:         { label: 'Width',          icon: <MoveHorizontal className="w-3.5 h-3.5" />, placeholder: 'Enter width',                              suffix: 'inches',       testid: 'cat-field-width' },
    gsm:           { label: 'GSM',            icon: <Grid3x3       className="w-3.5 h-3.5" />, placeholder: 'Enter GSM (e.g., 150 GSM, 180 GSM)',       suffix: 'GSM',          testid: 'cat-field-gsm' },
    quantity_unit: { label: 'Quantity Unit',  icon: <Package       className="w-3.5 h-3.5" />, placeholder: 'Select unit',                              select: ['metre', 'yard', 'piece'], testid: 'cat-field-quantity-unit' },
    dimension:     { label: 'Dimension — Width × Length', icon: <Ruler className="w-3.5 h-3.5" />, placeholder: 'Enter dimension',                       suffix: 'inches',       testid: 'cat-field-dimension' },
    size:          { label: 'Size',           icon: <Tag           className="w-3.5 h-3.5" />, placeholder: isKidsWear ? 'Enter size (e.g., 1-2 Y, 2-3 Y, 3-4 Y)' : 'Enter size (e.g., S, M, L, XL, XXL)', testid: 'cat-field-size' },
    fabric:        { label: 'Fabric',         icon: <Shirt         className="w-3.5 h-3.5" />, placeholder: 'Enter fabric',                             testid: 'cat-field-fabric' },
    pattern_style: { label: 'Pattern / Style',icon: <Palette       className="w-3.5 h-3.5" />, placeholder: 'Enter pattern or style',                   testid: 'cat-field-pattern' },
    fit:           { label: 'Fit',            icon: <Shirt         className="w-3.5 h-3.5" />, placeholder: cat === 'Ladies Wear' ? 'Enter fit (e.g., Regular, Slim Fit, A-line)' : 'Enter fit (e.g., Regular, Slim Fit, Relaxed Fit)', testid: 'cat-field-fit' },
  };

  return (
    <div
      className="sm:ml-9 mt-6 pt-5 border-t border-fab-primary-lighter"
      data-testid="category-specific-fields"
      data-category={cat}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {fieldSet.map((k) => {
          const cfg = FIELD_MAP[k];
          if (!cfg) return null;
          const val = manual[k] || '';
          const onChange = (v) => setManual({ ...manual, [k]: v });
          return (
            <div key={k} className={k === 'dimension' && fieldSet.length === 2 ? 'sm:col-span-2 md:col-span-2' : k === 'gsm' && cat === 'Girls Wear' ? 'sm:col-span-2 md:col-span-3' : k === 'gsm' && cat === 'Boys Wear' ? 'sm:col-span-2 md:col-span-3' : ''}>
              <label className="text-[12px] font-semibold text-fab-text-secondary flex items-center gap-1.5 text-fab-primary">
                <span className="text-fab-primary">{cfg.icon}</span>
                <span className="text-fab-text-secondary">{cfg.label}</span>
              </label>
              {cfg.select ? (
                <div className="relative mt-1">
                  <select
                    data-testid={cfg.testid}
                    value={val}
                    onChange={(e) => onChange(e.target.value)}
                    className="fab-input w-full px-3 py-2 pr-8 rounded-lg border border-fab-primary-lighter text-[13px] appearance-none bg-white"
                  >
                    <option value="" disabled>{cfg.placeholder}</option>
                    {cfg.select.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <ArrowRight className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-fab-text-muted pointer-events-none" />
                </div>
              ) : (
                <div className="relative mt-1">
                  <input
                    data-testid={cfg.testid}
                    type="text"
                    value={val}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={cfg.placeholder}
                    className={`fab-input w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] ${cfg.suffix ? 'pr-16' : ''}`}
                  />
                  {cfg.suffix && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-fab-text-muted uppercase tracking-wide">
                      {cfg.suffix}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Manual Product Type field (Feb 2026 final spec) ────────────────────────
// REQUIRED for every business category. Free-text input — never a dropdown
// (spec §3 prohibits controlled lists). Placeholder + helper adapt to the
// picked category using the table below. Icon + typography reuse the same
// visual language as MandatoryField / CategorySpecificFields so it drops
// cleanly into the existing Step 4 layout.
const PRODUCT_TYPE_HINTS = {
  'Fabric':      { placeholder: 'Enter product type (e.g., Cotton, Rayon, Georgette, Chiffon, Linen)',
                   helper: 'Enter the specific type of fabric you are adding.' },
  'Ladies Wear': { placeholder: 'Enter product type (e.g., Saree, Kurti, Dress, Blouse, Dress Material)',
                   helper: 'Enter the specific type of ladies wear product you are adding.' },
  "Men's Wear":  { placeholder: 'Enter product type (e.g., Shirt, T-Shirt, Kurta, Jeans, Trouser)',
                   helper: "Enter the specific type of men's wear product you are adding." },
  'Girls Wear':  { placeholder: 'Enter product type (e.g., Frock, Dress, Top, Skirt, Lehenga)',
                   helper: "Enter the specific type of girl's wear product you are adding." },
  'Boys Wear':   { placeholder: 'Enter product type (e.g., Shirt, T-Shirt, Kurta, Jeans, Shorts)',
                   helper: "Enter the specific type of boy's wear product you are adding." },
  'Furnishing':  { placeholder: 'Enter product type (e.g., Bedsheet, Curtain, Cushion Cover, Table Cover)',
                   helper: 'Enter the specific type of furnishing product you are adding.' },
};
const PRODUCT_TYPE_FALLBACK = {
  placeholder: 'Enter product type',
  helper: 'Enter the specific type of product you are adding.',
};

function ProductTypeField({ pickedCatId, value, onChange }) {
  const cfg = PRODUCT_TYPE_HINTS[(pickedCatId || '').trim()] || PRODUCT_TYPE_FALLBACK;
  return (
    <div className="sm:ml-9 mt-6 pt-5 border-t border-fab-primary-lighter" data-testid="product-type-field-block" data-category={(pickedCatId || '').trim()}>
      <label className="text-[12px] font-semibold text-fab-text-secondary flex items-center gap-1.5">
        <Layers className="w-3.5 h-3.5 text-fab-primary" />
        <span>Product Type <span className="text-red-500">*</span></span>
      </label>
      <input
        data-testid="product-type-input"
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={cfg.placeholder}
        className="fab-input mt-1 w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px]"
      />
      <div className="mt-1 text-[11px] text-fab-text-muted" data-testid="product-type-helper">{cfg.helper}</div>
    </div>
  );
}
